<template>
	<div>
		<div class="characteristic-edit">
			<div v-if="selected && selected.length > 0" class="characteristic-edit--selected-items">
				<SelectedItems
					:characteristic-items="characteristicItems"
					:characteristic-type="characteristicType"
					:selected="selectedItems"
					:show-experience="selectedItemsConfig ? selectedItemsConfig.showExperience : undefined"
					:flat="selectedItemsConfig ? selectedItemsConfig.flat : undefined"
					class="no-left-padding"
					:icon-path="iconPath"
					:no-children-appendix="selectedItemsConfig ? selectedItemsConfig.noChildrenAppendix : undefined"
					:label-type="selectedItemsConfig ? selectedItemsConfig.labelType : undefined"
					:styling="selectedItemsConfig ? selectedItemsConfig.styling : undefined"
					:sort-type="selectedItemsConfig ? selectedItemsConfig.sortType : undefined"
					@click="openFan"
				/>
			</div>
			<div
				v-if="nothingAllowed && nothingAllowed.overall && !nothingAllowed.overall.inFan && !hasSelectedItems"
				class="overall"
			>
				<b-form-checkbox
					v-model="nothingAllowedOverall"
					@input="nothingAllowedOverallChanged"
				>
					{{ nothingAllowed.overall.text }}
				</b-form-checkbox>
			</div>
			<div v-if="showNothingAllowedOverallInfo" class="overall-info">{{ nothingAllowed.overall.text }}</div>
			<div v-if="withEditButton" :class="'text-' + openFanButtonAlignment">
				<Button
					v-if="!nothingAllowedOverall || isNothingAllowedOverallInFan"
					:text="computedOpenFanLabel"
					secondary
					smaller
					@click="openFan"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from "@/vue/icon/Icon"
import CharacteristicLabel from "@/vue/characteristic/Label";
import {mapActions, mapState} from "vuex";
import _ from "lodash";
import {uuid} from "vue-uuid";
import {arrayIsNotEmpty} from "@/helper/Array";
import SelectedItems from "@/vue/characteristic/SelectedItems";
import Button from "@/vue/common/Button";

export default {

	components: {
		Button,
		SelectedItems,
		CharacteristicLabel,
		Icon,
	},

	props: {
		value: {
			type: Array,
			required: false,
		},
		title: {
			type: String,
			required: false,
		},
		subTitle: {
			type: String,
			required: false,
		},
		titleFormats: {
			type: Object,
			required: false,
		},
		characteristicType: {
			type: Object,
			required: true,
		},
		characteristicItems: {
			type: Array,
			required: true,
		},
		withKeyword: {
			type: Boolean,
			required: false,
			default: false,
		},
		keywordPlaceholder: {
			type: String,
			required: false,
		},
		nothingAllowed: {
			type: Object,
			required: false,
		},
		supplementsConfig: {
			type: Object,
			required: false,
		},
		withExperience: {
			type: Boolean,
			required: false,
			default: false,
		},
		withSelfAssessment: {
			type: Boolean,
			required: false,
			default: false,
		},
		withEducation: {
			type: Boolean,
			required: false,
			default: false,
		},
		withRequirement: {
			type: Boolean,
			required: false,
			default: false,
		},
		experienceConfig: {
			type: Object,
			required: false,
		},
		singleChoice: {
			type: Boolean,
			required: false,
			default: false,
		},
		openFanLabel: {
			type: String,
			required: false,
			default: 'Auswählen'
		},
		openFanLabelWithSelection: {
			type: String,
			required: false,
		},
		labelType: {
			type: String,
			required: false,
		},
		labelSet: {
			type: String,
			required: false,
			default: 'application'
		},
		selectedItemsConfig: {
			type: Object,
			required: false,
		},
		withEditButton: {
			type: Boolean,
			required: false,
			default: true,
		},
		iconPath: {
			type: String,
			required: false,
		},
		descriptionConfig: {
			type: Object,
			required: false
		},
		groupByLetter: {
			type: Object,
			required: false
		},
		showMultipleHint: {
			type: Boolean,
			required: false,
			default: false,
		},
		openFanButtonAlignment: {
			type: String,
			required: false,
			default: 'right',
		},
		headerInfo: {
			type: String,
			required: false,
		},
		completionCheckConfig: {
			type: Object,
			required: false,
		},
		additionsConfig: {
			type: Object,
			required: false,
		},
		searchConfig: {
			type: Object,
			required: false,
		}
	},

	data ()
	{
		return {
			key: uuid.v4(),
			selected: this.value,
			nothingAllowedOverall: this.nothingAllowed?.overall && _.isArray(this.value) && this.value.length === 0,
		};
	},

	computed: {
		...mapState('characteristic/fan', {
			'fanShown': 'show',
			'fanSelected': 'selected',
		}),
		isNothingAllowedOverallInFan ()
		{
			return this.nothingAllowed?.overall?.inFan === true;
		},
		showNothingAllowedOverallInfo ()
		{
			return this.isNothingAllowedOverallInFan
				&& this.nothingAllowedOverall;
		},
		hasSelectedItems ()
		{
			return arrayIsNotEmpty(this.selected);
		},
		hasAnySelection ()
		{
			return this.hasSelectedItems || this.nothingAllowedOverall;
		},
		computedOpenFanLabel ()
		{
			if (!this.hasAnySelection)
			{
				return this.openFanLabel;
			}

			return this.openFanLabelWithSelection || this.openFanLabel;
		},
		valid ()
		{
			if (this.nothingAllowed?.overall && this.nothingAllowedOverall)
			{
				return true;
			}

			return this.hasSelectedItems;
		},
		selectedItems ()
		{
			if (this.selectedItemsConfig?.lastLevelOnly)
			{
				return _.filter(this.selected, item =>
				{
					return item.item.children.length === 0;
				});
			}

			return this.selected;
		},
	},

	methods: {
		...mapActions('characteristic/fan', {
			'showFan': 'show'
		}),
		nothingAllowedOverallChanged (checked)
		{
			if (checked)
			{
				this.selected = [];
			}

			this.emitChangeEvent(true);
		},
		openFan ()
		{
			this.showFan({
				key: this.key,
				selected: this.selected,
				characteristicType: this.characteristicType,
				characteristicItems: this.characteristicItems,
				title: this.title,
				subTitle: this.subTitle,
				titleFormats: this.titleFormats,
				withKeyword: this.withKeyword,
				keywordPlaceholder: this.keywordPlaceholder,
				nothingAllowed: this.nothingAllowed,
				supplementsConfig: this.supplementsConfig,
				withExperience: this.withExperience,
				withSelfAssessment: this.withSelfAssessment,
				withEducation: this.withEducation,
				withRequirement: this.withRequirement,
				singleChoice: this.singleChoice,
				experienceConfig: this.experienceConfig,
				labelType: this.labelType,
				labelSet: this.labelSet,
				iconPath: this.iconPath,
				descriptionConfig: this.descriptionConfig,
				groupByLetter: this.groupByLetter,
				showMultipleHint: this.showMultipleHint,
				headerInfo: this.headerInfo,
				completionCheckConfig: this.completionCheckConfig,
				additionsConfig: this.additionsConfig,
				searchConfig: this.searchConfig,
				onChange: ({selected}) =>
				{
					if (selected === undefined)
					{
						this.selected = undefined;
						this.nothingAllowedOverall = false;

						this.emitChangeEvent();

						return;
					}

					this.selected = _.cloneDeep(selected);

					if (arrayIsNotEmpty(this.selected))
					{
						this.nothingAllowedOverall = false;
					}

					if (Array.isArray(this.selected) && this.selected.length === 0 && this.isNothingAllowedOverallInFan)
					{
						this.nothingAllowedOverall = true;
					}

					this.emitChangeEvent();
				},
				onClose: ({skipped}) =>
				{
					this.emitChangeEvent(true, skipped);
				},
			});
		},
		changeSelected (selected)
		{
			this.selected = selected;
		},
		emitChangeEvent (triggerFinishEvent, skipped)
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', this.selected);

			if (triggerFinishEvent)
			{
				this.$emit('finish', {
					items: this.selected,
					skipped
				});
			}
		}
	}
}
</script>
